/*
 * Reusable two-axis admin layout: top brandbar (existing app shell) →
 * horizontal category menubar → left sub-menu sidebar → main content.
 * Both the workspace app's "Administration" surface and the platform
 * admin app's settings group will mount this shell with different
 * `categories` configs.
 *
 * Active category is derived from the current pathname so deep links
 * (e.g. /app/administration/connections/instances) restore both the
 * top-tab and sub-menu selection on load.
 */

import { ReactNode, useMemo } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AuthSession, clearSession, displayName } from "../lib/api.js";

export interface AdminCategoryItem {
  to: string;          // absolute path, e.g. "/app/administration/connections/instances"
  label: string;
  show?: boolean;      // permission gate; `false` hides; default true
}

export interface AdminCategory {
  id: string;          // segment that appears in the URL after the base path
  label: string;
  show?: boolean;      // permission gate at the category level
  items: AdminCategoryItem[];
}

export interface AdministrationShellProps {
  session: AuthSession;
  basePath: string;    // e.g. "/app/administration"
  brandLabel: string;  // e.g. "TallyStream · Workspace"
  brandLogo?: string;  // optional path; defaults to /brand-mark-light.svg
  brandHref?: string;  // where the brand pill links back to (default: basePath)
  categories: AdminCategory[];
  children: ReactNode; // routed page content
  /** Optional escape hatch: a "Back to {appLabel}" link that takes
      the user out of administration and back to the main app shell. */
  backTo?: { to: string; label: string };
}

function Avatar({ name }: { name: string }) {
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  return <span className="avatar" aria-hidden>{initial}</span>;
}

function deriveActiveCategoryId(pathname: string, basePath: string, categories: AdminCategory[]): string {
  // pathname shape: <basePath>/<categoryId>/<...>. If the URL hasn't
  // descended into a category yet (just basePath), default to the first
  // visible category so the sub-menu sidebar isn't empty.
  if (!pathname.startsWith(basePath)) return categories[0]?.id ?? "";
  const tail = pathname.slice(basePath.length).replace(/^\//, "");
  const head = tail.split("/", 1)[0];
  if (head && categories.some((c) => c.id === head && c.show !== false)) return head;
  return categories.find((c) => c.show !== false)?.id ?? "";
}

export default function AdministrationShell({
  session,
  basePath,
  brandLabel,
  brandLogo = "/brand-mark-light.svg",
  brandHref,
  categories,
  children,
  backTo
}: AdministrationShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const who = displayName();
  const onSignOut = () => {
    clearSession();
    navigate("/login", { replace: true });
  };
  const visibleCategories = useMemo(
    () => categories.filter((c) => c.show !== false && c.items.some((i) => i.show !== false)),
    [categories]
  );
  const activeCategoryId = deriveActiveCategoryId(location.pathname, basePath, visibleCategories);
  const activeCategory = visibleCategories.find((c) => c.id === activeCategoryId);
  const visibleItems = activeCategory?.items.filter((i) => i.show !== false) ?? [];

  return (
    <div className="shell shell-admin shell-administration">
      <header className="shell-header shell-header-admin">
        <Link to={brandHref ?? basePath} className="brand">
          <img src={brandLogo} alt="" />
          <span>{brandLabel}</span>
        </Link>
        <span className="header-chip" style={{ background: "#1f2937", color: "#f9fafb" }}>
          Administration
        </span>
        <div className="shell-header-spacer" />
        {backTo && (
          <Link to={backTo.to} className="header-back-link" style={{ marginRight: 12 }}>
            ← {backTo.label}
          </Link>
        )}
        <span className="user-pill">
          <Avatar name={who} />
          <span>{who}</span>
          <span className="pill pill-info" style={{ textTransform: "uppercase" }}>
            {session.role === "admin" ? "Platform admin" : session.role}
          </span>
        </span>
        <button className="signout" type="button" onClick={onSignOut}>Sign out</button>
      </header>

      {/* Horizontal category menubar — top-level grouping. */}
      <nav className="admin-categories" aria-label="Administration categories">
        {visibleCategories.map((c) => {
          // Use the FIRST visible item as the navigation target so clicking
          // a top-tab lands the user on a real page rather than an empty
          // category root.
          const firstItem = c.items.find((i) => i.show !== false);
          const target = firstItem?.to ?? `${basePath}/${c.id}`;
          const isActive = activeCategoryId === c.id;
          return (
            <Link
              key={c.id}
              to={target}
              className={`admin-category-tab${isActive ? " admin-category-tab-active" : ""}`}
            >
              {c.label}
            </Link>
          );
        })}
      </nav>

      {/* Sub-menu sidebar — items for the active category. */}
      <aside className="shell-sidebar admin-submenu" aria-label={`${activeCategory?.label ?? "Administration"} sub-menu`}>
        {activeCategory && (
          <>
            <div className="sidebar-section"><div className="sidebar-section-title">{activeCategory.label}</div></div>
            <nav className="sidebar-nav">
              {visibleItems.map((item) => (
                <NavLink key={item.to} to={item.to} end>
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </>
        )}
      </aside>

      <main className="shell-main">{children}</main>
    </div>
  );
}
